import { ImageData } from '@/types/globals'
import React, { createContext, useContext, ReactNode, useState } from 'react'
import useUndo from 'use-undo'

// Define the context type
type ImageContextType = {
  singleImage: ImageData | null
  imageArray: ImageData[]
  addSingleImage: (key: string, url: string) => void
  updateSingleImage: (image: ImageData | null) => void
  addImagesToArray: (images: ImageData | ImageData[]) => void
  removeImageFromArray: (key: string) => void
  clearAllImages: () => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
  isDraw: boolean
  isErase: boolean
  handleIsErase: () => void
  handleIsDraw: (draw?: boolean) => void
  bgRemove: boolean
  handleBgRemove: (remove: boolean) => void
  navigateToPrevImage: () => void
  navigateToNextImage: () => void
  updateImageKey: (newKey: string) => void
  updateImageUrl: (newUrl: string, key: string) => void
  handleScaleSlide: (show?: boolean) => void
  scaleSlide: boolean
  handleAssetDuplicate: (duplicate?: boolean) => void
  assetDuplicate: boolean
  handleOverlayImage: (image?: string) => void
  overlayImage: string
  overlaySave: boolean
  setOverlaySave: (save: boolean) => void
  setShowResizeModal: (show: boolean) => void
  showResizeModal: boolean
}

// Create the context
const ImageContext = createContext<ImageContextType | undefined>(undefined)

export const ImageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [
    singleImageState,
    {
      set: setSingleImage,
      reset: resetSingleImage,
      undo,
      redo,
      canUndo,
      canRedo,
    },
  ] = useUndo<ImageData | null>(null)
  const [imageArrayState, { set: setImageArray }] = useUndo<ImageData[]>([])

  const singleImage = singleImageState.present
  const imageArray = imageArrayState.present

  const [isDraw, setIsDraw] = useState(false)
  const [isErase, setIsErase] = useState(false)
  const [bgRemove, setBgRemove] = useState(false)
  const [scaleSlide, setScaleSlide] = useState(false)
  const [overlayImage, setOverlayImage] = useState<string>('')
  const [overlaySave, setOverlaySave] = useState<boolean>(false)
  const [showResizeModal, setShowResizeModal] = useState<boolean>(false)
  const [assetDuplicate, setAssetDuplicate] = useState(false)
  const addSingleImage = (key: string, url: string) => {
    resetSingleImage({ key, url })
  }

  const updateSingleImage = (image: ImageData | null) => {
    setSingleImage(image)
  }

  const addImagesToArray = (images: ImageData | ImageData[]) => {
    setImageArray([
      ...imageArray,
      ...(Array.isArray(images) ? images : [images]),
    ])
  }

  const removeImageFromArray = (key: string) => {
    setImageArray(imageArray.filter((image) => image.key !== key))
  }

  const clearAllImages = () => {
    setSingleImage(null)
    setImageArray([])
    setOverlayImage('')
  }

  const handleIsDraw = (draw?: boolean) => {
    setIsDraw(draw === false ? false : !isDraw)
    setIsErase(false)
  }

  const handleBgRemove = (remove: boolean) => {
    setBgRemove(remove)
  }

  const handleIsErase = () => {
    setIsErase(!isErase)
  }
  const handleScaleSlide = (show?: boolean) => {
    setScaleSlide(show || !scaleSlide)
  }

  const handleAssetDuplicate = (duplicate?: boolean) => {
    setAssetDuplicate(duplicate || !assetDuplicate)
  }

  const handleOverlayImage = (image?: string) => {
    setOverlayImage(image || '')
  }

  const navigateToPrevImage = () => {
    if (imageArray.length > 0 && singleImage) {
      const currentIndex = imageArray.findIndex(
        (img) => img.key === singleImage.key
      )
      const prevIndex =
        (currentIndex - 1 + imageArray.length) % imageArray.length
      const { key, url } = imageArray[prevIndex]
      addSingleImage(key, url)
    }
  }

  const navigateToNextImage = () => {
    if (imageArray.length > 0 && singleImage) {
      const currentIndex = imageArray.findIndex(
        (img) => img.key === singleImage.key
      )
      const nextIndex = (currentIndex + 1) % imageArray.length
      const { key, url } = imageArray[nextIndex]
      addSingleImage(key, url)
    }
  }

  const updateImageKey = (newKey: string) => {
    if (singleImage && newKey.trim()) {
      updateSingleImage({ key: newKey, url: singleImage.url })

      // Also update the key in the imageArray
      const updatedArray = imageArray.map((img) =>
        img.url === singleImage.url ? { ...img, key: newKey } : img
      )
      setImageArray(updatedArray)
    }
  }

  const updateImageUrl = (newUrl: string, key: string) => {
    const updatedArray = imageArray.map((img) =>
      img.key === key ? { ...img, url: newUrl } : img
    )
    setImageArray(updatedArray)
  }

  return (
    <ImageContext.Provider
      value={{
        singleImage,
        imageArray,
        addSingleImage,
        updateSingleImage,
        addImagesToArray,
        removeImageFromArray,
        clearAllImages,
        undo,
        redo,
        canUndo,
        canRedo,
        isDraw,
        isErase,
        bgRemove,
        handleBgRemove,
        handleIsDraw,
        handleIsErase,
        navigateToPrevImage,
        navigateToNextImage,
        updateImageKey,
        updateImageUrl,
        handleScaleSlide,
        scaleSlide,
        handleAssetDuplicate,
        assetDuplicate,
        handleOverlayImage,
        overlayImage,
        setOverlaySave,
        overlaySave,
        setShowResizeModal,
        showResizeModal,
      }}
    >
      {children}
    </ImageContext.Provider>
  )
}

// Hook to use ImageContext
export const useImageContext = () => {
  const context = useContext(ImageContext)
  if (!context) {
    throw new Error('useImageContext must be used within an ImageProvider')
  }
  return context
}
